// ResetGroup.tsx
import React from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { PedidoGroup } from '../../../Configuracion/types';
import { resetPedidoGroup } from '../../../../../crud/pedidos_api/pedidos';
import { getAutoPrintCancellationComandas } from '../../../../../crud/localDB/pedidosConfig';
import { toast } from 'react-toastify';
import { X } from 'react-bootstrap-icons';
import './ResetGroup.scss';

interface ResetGroupProps {
  pedidoGroup: PedidoGroup;
  show: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const ResetGroup: React.FC<ResetGroupProps> = ({ pedidoGroup, show, onClose, onSuccess }) => {
  const [resetting, setResetting] = React.useState(false);
  const [comment, setComment] = React.useState('');
  const [printToKitchen, setPrintToKitchen] = React.useState(false);
  const [autoPrintCancellationComandas, setAutoPrintCancellationComandas] = React.useState(false);

  React.useEffect(() => {
    let isMounted = true;

    getAutoPrintCancellationComandas()
      .then((value) => {
        if (!isMounted) return;
        setAutoPrintCancellationComandas(value);
        if (value) {
          setPrintToKitchen(true);
        }
      })
      .catch((error) => {
        console.error('Error loading cancellation comanda config:', error);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleResetGroup = async () => {
    if (comment.trim().length < 15) {
      toast.error('El comentario debe tener al menos 15 caracteres');
      return;
    }
    
    setResetting(true);
    try {
      await resetPedidoGroup(
        pedidoGroup.pedido_group_id,
        comment.trim(),
        autoPrintCancellationComandas || printToKitchen
      );
      toast.success('El grupo se ha vaciado correctamente');
      
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('pedido-group-reset', {
        detail: { pedido_group_id: pedidoGroup.pedido_group_id }
      }));
      
      onClose();
      
      // Call success callback if provided (for refreshing data)
      if (onSuccess) {
        onSuccess();
      } else {
        // Fallback to reload if no callback provided
        window.location.reload();
      }
    } catch (error) {
      toast.error('Error al vaciar el grupo');
      console.error('Error resetting pedido group, ID:', pedidoGroup.pedido_group_id, error);
    } finally {
      setResetting(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} className="reset-group-modal">
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">Confirmación</Modal.Title>
        </div>
        <Button variant="none" className="modal-close-button" onClick={onClose}>
          <X size={24} />
        </Button>
      </Modal.Header>
      <Modal.Body>
        <p>
          ¿Quieres vaciar el grupo <strong>{pedidoGroup.name}</strong>? 
          Al hacer esto, se cancelarán todos los pedidos activos del grupo.
        </p>
        <Form.Group className="mt-3">
          <Form.Label>
            Comentario <span className="comment-required">*</span>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Ingresa un comentario (mínimo 15 caracteres)"
            disabled={resetting}
            maxLength={500}
            className="reset-group-comment-textarea"
          />
          <Form.Text className="text-muted">
            {comment.trim().length}/15 caracteres mínimos
          </Form.Text>
        </Form.Group>
        {autoPrintCancellationComandas ? (
          <div className="print-to-kitchen-toggle text-muted">
            Se notificará a cocina automáticamente por configuración.
          </div>
        ) : (
          <Form.Group className="print-to-kitchen-toggle">
            <Form.Check
              type="switch"
              id="print-to-kitchen-reset-group"
              label="Notificar a cocina"
              checked={printToKitchen}
              onChange={(e) => setPrintToKitchen(e.target.checked)}
              disabled={resetting}
            />
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button 
            className="form-button-cancel" 
            variant="none" 
            onClick={onClose}
            disabled={resetting}
          >
            Cancelar
          </Button>
          <Button 
            className="form-button-create" 
            variant="none" 
            onClick={handleResetGroup}
            disabled={resetting || comment.trim().length < 15}
          >
            {resetting ? 'Vaciando...' : 'Vaciar Grupo'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ResetGroup;
